import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Grid,
  Divider,
  Alert,
  Chip,
  Paper,
  useTheme,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  IconButton,
  Collapse,
  DialogContentText,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { 
  Person, 
  CalendarToday, 
  LocalHospital,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  ExpandLess as ExpandLessIcon,
  AccessTime as AccessTimeIcon,
  Assignment as AssignmentIcon,
  MedicalServicesOutlined,
  AssignmentOutlined,
  Description as DescriptionIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
  Visibility as VisibilityIcon,
  FolderOpen as FolderIcon
} from '@mui/icons-material';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { updatePrescription, fetchPlanOfCareByUserId, fetchHealthDocuments } from '../../services/healinApi';

function PrescriptionDetail({ prescription: initialPrescription, open, onClose, onIssuePrescription, onIssued, onClaimed }) {
  const theme = useTheme();
  const [prescription, setPrescription] = useState(initialPrescription);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [issuingPrescription, setIssuingPrescription] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [expandedMedication, setExpandedMedication] = useState(null);
  const [editingMedicationId, setEditingMedicationId] = useState(null);
  const [editedDosage, setEditedDosage] = useState('');
  const [editedInstructions, setEditedInstructions] = useState('');
  const [planOfCare, setPlanOfCare] = useState(null);
  const [planOfCareLoading, setPlanOfCareLoading] = useState(false);
  const [healthDocuments, setHealthDocuments] = useState(null);
  const [healthDocumentsLoading, setHealthDocumentsLoading] = useState(false);
  const [healthDocumentsError, setHealthDocumentsError] = useState(null);
  const [showHealthDocuments, setShowHealthDocuments] = useState(false);

    const { enqueueSnackbar } = useSnackbar();

  const fetchPlanOfCare = useCallback(async () => {
    if (!prescription?.userId || prescription?.status !== 'CLAIMED') return;

    setPlanOfCareLoading(true);
    
    try {
      console.log('Fetching plan of care for user:', prescription.userId);
      const planData = await fetchPlanOfCareByUserId(prescription.userId);
      setPlanOfCare(planData);
      console.log('Plan of care loaded:', planData);
    } catch (error) {
      console.error('Error fetching plan of care:', error);
      // Silently handle errors - plan of care is supplementary information
    } finally {
      setPlanOfCareLoading(false);
    }
  }, [prescription?.userId, prescription?.status]);

  // Fetch Plan of Care only for claimed prescriptions
  useEffect(() => {
    if (prescription && prescription.userId && prescription.status === 'CLAIMED') {
      fetchPlanOfCare();
    }
  }, [prescription, fetchPlanOfCare]);

  const handleCheckHealthDocuments = async () => {
    if (!prescription?.userId || !planOfCare?.profileID) {
      enqueueSnackbar('Unable to fetch health documents: missing user or profile information', { variant: 'error' });
        return;
      }
      
    setHealthDocumentsLoading(true);
    setHealthDocumentsError(null);
    
    try {
      console.log('Fetching health documents for:', {
        userId: prescription.userId,
        identityId: planOfCare.profileID
      });
      
      const documentsData = await fetchHealthDocuments(prescription.userId, planOfCare.profileID);
      setHealthDocuments(documentsData);
      setShowHealthDocuments(true);
      enqueueSnackbar('Health documents loaded successfully', { variant: 'success' });
      console.log('Health documents loaded:', documentsData);
    } catch (error) {
      console.error('Error fetching health documents:', error);
      setHealthDocumentsError(error.message);
      enqueueSnackbar(`Error loading health documents: ${error.message}`, { variant: 'error' });
    } finally {
      setHealthDocumentsLoading(false);
    }
  };

 

  const handleToggleExpand = (medicationId) => {
    setExpandedMedication(expandedMedication === medicationId ? null : medicationId);
  };

  const handleEditMedication = (medicationId) => {
    const medication = findMedicationById(medicationId);
    if (medication) {
      setEditingMedicationId(medicationId);
      setEditedDosage(getMedicationDosage(medication));
      setEditedInstructions(getMedicationInstructions(medication));
    }
  };

  const handleCancelEdit = () => {
    setEditingMedicationId(null);
    setEditedDosage('');
    setEditedInstructions('');
  };

  const handleSaveMedication = (medicationId) => {
    const updatedMedications = prescription.medications.map(med => {
      const currentMedId = typeof med === 'string' ? med : (med.id || med.medicationId);
      if (currentMedId === medicationId) {
        // If med is a string (just an ID), create a new object
        if (typeof med === 'string') {
          return {
            medicationId: med,
            dosage: editedDosage,
            instructions: editedInstructions
          };
        } 
        // Otherwise update the existing object
        return {
          ...med,
          dosage: editedDosage,
          instructions: editedInstructions
        };
      }
      return med;
    });

    setPrescription({
      ...prescription,
      medications: updatedMedications
    });
    
    setEditingMedicationId(null);
    // Here you would typically call an API to update the prescription
  };

  // Helper to find a medication by ID
  const findMedicationById = (medicationId) => {
    return prescription.medications.find(med => {
      const currentMedId = typeof med === 'string' ? med : (med.id || med.medicationId);
      return currentMedId === medicationId;
    });
  };

  const handleOpenConfirmDialog = () => {
    setConfirmDialogOpen(true);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
  };



  const handleIssuePrescription = async () => {
    try {
    setIssuingPrescription(true);
    
      console.log('Issuing prescription for:', {
        userId: prescription.userId,
        requestId: prescription.requestId,
        notes: notes
      });
      
      // Call the API to update prescription status from CLAIMED to ISSUED
      const response = await updatePrescription({
        userId: prescription.userId,
        requestId: prescription.requestId,
        status: 'ISSUED',
        comments: notes || 'Prescription issued successfully',
        source: 'portal'
      });
      
      console.log('Prescription issued successfully:', response);
      
      // Update the prescription object with new status
      const updatedPrescription = {
        ...prescription,
        status: 'ISSUED',
        issueDate: new Date().toISOString(),
        notes: notes
      };
      
      // Call the parent callback if provided
      if (onIssued) {
        onIssued(updatedPrescription);
      }
      
      // Close the dialog and show success
          setConfirmDialogOpen(false);
      enqueueSnackbar('Prescription issued successfully', { variant: 'success' });
      
      // Close the detail view after a short delay
      setTimeout(() => {
        onClose();
      }, 1500);
      
    } catch (error) {
          console.error('Error issuing prescription:', error);
      enqueueSnackbar(`Error issuing prescription: ${error.message}`, { variant: 'error' });
    } finally {
      setIssuingPrescription(false);
    }
  };

  const handleClaimPrescription = async () => {
    try {
      setIsSubmitting(true);
      
      // Get the medications in the correct format for the API
      const formattedMedications = prescription.medications.map(med => {
        // If med is already a string (just an ID), return it
        if (typeof med === 'string') return med;
        // Otherwise return the ID
        return med.medicationId || med.id;
      });

      // Prepare claim payload
      const claimPayload = {
        requestId: prescription.requestId || prescription.id,
        medications: formattedMedications,
        notes: notes || '' // Use the notes state as notes for claiming
      };

      console.log('Claiming prescription with payload:', claimPayload);
      
      // In a real app, you would call your API here
      // const response = await claimPrescription(claimPayload);
      
      // For demo, simulate success
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      onClaimed({
        ...prescription,
        status: 'CLAIMED',
        claimDate: new Date().toISOString(),
        notes: notes
      });
      
      enqueueSnackbar('Prescription claimed successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error claiming prescription:', error);
      enqueueSnackbar(`Error claiming prescription: ${error.message}`, { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Helper functions to get medication details
  const getMedicationName = (medication) => {
    if (typeof medication === 'string') return 'Unknown Medication';
    // Try all possible property names for medication name
    return medication.medicationName || medication.name || 
           (medication.medication ? medication.medication.name : 'Unnamed Medication');
  };

  const getMedicationDosage = (medication) => {
    if (typeof medication === 'string') return 'Not specified';
    return medication.dosage || 
           (medication.medication ? medication.medication.dosage : 'Not specified');
  };

  const getMedicationInstructions = (medication) => {
    if (typeof medication === 'string') return 'No specific instructions';
    return medication.instructions || medication.posology || 
           (medication.medication ? (medication.medication.instructions || medication.medication.posology) : 'No specific instructions');
  };

  // Render a medication item with all its details
  const renderMedicationItem = (medication, index) => {
    // Debug: Log the medication structure to help identify issues
    console.log(`Medication ${index}:`, medication);
    
    const medicationName = getMedicationName(medication);
    const medicationDosage = getMedicationDosage(medication);
    const medicationInstructions = getMedicationInstructions(medication);
    
    const medicationId = typeof medication === 'string' ? medication : (medication.id || medication.medicationId);

    return (
      <Card 
        key={medicationId || index} 
        variant="outlined" 
        sx={{ 
          mb: 2, 
          borderLeft: `4px solid ${theme.palette.primary.main}`,
          bgcolor: editingMedicationId === medicationId ? 'rgba(0, 0, 0, 0.02)' : 'transparent'
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Typography variant="h6" component="h4">
              {medicationName}
            </Typography>
            <Box>
              {editingMedicationId === medicationId ? (
                <>
                  <IconButton size="small" onClick={() => handleSaveMedication(medicationId)} color="primary">
                    <SaveIcon />
                  </IconButton>
                  <IconButton size="small" onClick={handleCancelEdit} color="error">
                    <CancelIcon />
                  </IconButton>
                </>
              ) : (
                <IconButton size="small" onClick={() => handleEditMedication(medicationId)}>
                  <EditIcon />
                </IconButton>
              )}
              <IconButton size="small" onClick={() => handleToggleExpand(medicationId)}>
                {expandedMedication === medicationId ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          </Box>

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2">Dosage:</Typography>
              {editingMedicationId === medicationId ? (
                <TextField
                  fullWidth
                  size="small"
                  value={editedDosage}
                  onChange={(e) => setEditedDosage(e.target.value)}
                  margin="dense"
                />
              ) : (
                <Typography variant="body2">{medicationDosage}</Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2">Frequency:</Typography>
              <Typography variant="body2">
                {typeof medication === 'string' ? 'Not specified' : (medication.frequency || 'Not specified')}
              </Typography>
            </Grid>
          </Grid>

          <Collapse in={expandedMedication === medicationId} timeout="auto" unmountOnExit>
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2">Instructions:</Typography>
              {editingMedicationId === medicationId ? (
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                  value={editedInstructions}
                  onChange={(e) => setEditedInstructions(e.target.value)}
                  margin="dense"
                />
              ) : (
                <Typography variant="body2">{medicationInstructions}</Typography>
              )}

              {typeof medication !== 'string' && medication.warnings && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="subtitle2">Warnings:</Typography>
                  <Typography variant="body2">{medication.warnings}</Typography>
                </Alert>
              )}

              {typeof medication !== 'string' && medication.sideEffects && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2">Potential Side Effects:</Typography>
                  <Typography variant="body2">{medication.sideEffects}</Typography>
                </Box>
              )}
            </Box>
          </Collapse>
        </CardContent>
      </Card>
    );
  };

  if (!prescription) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6">No prescription selected</Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}>
              <Person />
            </Avatar>
            <Box>
              <Typography variant="h5" component="h2">
                {prescription.patientName || 'Unknown Patient'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Patient ID: {prescription.patientId || 'N/A'}
              </Typography>
            </Box>
          </Box>
          <Chip 
            label={prescription.status || 'Unknown'} 
            color={prescription.status === 'CLAIMED' ? 'primary' : 'default'}
            sx={{ fontWeight: 'bold' }}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                avatar={<InfoIcon color="primary" />}
                title="Patient Information"
                titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
                sx={{ pb: 1 }}
              />
              <CardContent sx={{ pt: 0 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <InfoIcon fontSize="small" sx={{ mr: 1, color: theme.palette.info.main, fontSize: '0.9rem' }} />
                      Age: {prescription.patientInfo?.age || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <InfoIcon fontSize="small" sx={{ mr: 1, color: theme.palette.info.main, fontSize: '0.9rem' }} />
                      Gender: {prescription.patientInfo?.gender || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <LocalHospital fontSize="small" sx={{ mr: 1, color: theme.palette.info.main, fontSize: '0.9rem' }} />
                      Blood Type: {prescription.patientInfo?.bloodType || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <InfoIcon fontSize="small" sx={{ mr: 1, color: theme.palette.info.main, fontSize: '0.9rem' }} />
                      Nationality: {prescription.patientInfo?.nationality || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <InfoIcon fontSize="small" sx={{ mr: 1, color: theme.palette.info.main, fontSize: '0.9rem' }} />
                      Health Data Number: {prescription.patientInfo?.healthDataNumber || prescription.userDetail?.healthDataNumber || prescription.userDetail?.HealthIDNumber || prescription.userDetail?.SNS_Number || prescription.userDetail?.HealthID || 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Contact Information:
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Email:</strong> {prescription.patientInfo?.email || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Phone:</strong> {prescription.patientInfo?.phone || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Address:</strong> {prescription.patientInfo?.address || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>City:</strong> {prescription.patientInfo?.city || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Postal Code:</strong> {prescription.patientInfo?.postalCode || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>Country:</strong> {prescription.patientInfo?.country || 'N/A'}
                    </Typography>
                  </Grid>
                  {prescription.patientInfo?.fiscalNumber && prescription.patientInfo.fiscalNumber !== 'N/A' && (
                    <Grid item xs={12}>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <strong>Fiscal Number:</strong> {prescription.patientInfo.fiscalNumber}
                      </Typography>
                    </Grid>
                  )}
                </Grid>

                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                  Chronic Conditions:
                </Typography>
                {/* Show conditions from Plan of Care for claimed prescriptions, otherwise fallback to patientInfo */}
                {(() => {
                  // Try to get conditions from Plan of Care first (only for claimed prescriptions)
                  let conditions = [];
                  
                  if (prescription.status === 'CLAIMED' && planOfCare && planOfCare.Pathways) {
                    try {
                      const pathways = JSON.parse(planOfCare.Pathways);
                      conditions = pathways.map(pathway => ({
                        name: pathway.patologia_de_base,
                        icd10: pathway.ICD10,
                        id: pathway.ID_Pathway
                      }));
                    } catch (error) {
                      console.error('Error parsing pathways:', error);
                    }
                  }
                  
                  // Fallback to patientInfo conditions if no pathways found or not a claimed prescription
                  if (conditions.length === 0 && prescription.patientInfo?.chronicConditions) {
                    conditions = prescription.patientInfo.chronicConditions.map(condition => ({
                      name: condition
                    }));
                  }
                  
                  return conditions.length > 0 ? (
                    <Box sx={{ mt: 0, mb: 2 }}>
                      <Grid container spacing={1}>
                        {conditions.map((condition, index) => (
                          <Grid item key={index}>
                            <Chip 
                              label={condition.icd10 ? `${condition.name} (${condition.icd10})` : condition.name}
                              color="secondary" 
                              variant="outlined"
                              size="small"
                              sx={{ mb: 1 }}
                            />
                          </Grid>
                        ))}
                      </Grid>
                      {conditions.some(c => c.icd10) && prescription.status === 'CLAIMED' && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Conditions shown with ICD-10 codes from Plan of Care
                      </Typography>
                      )}
                  </Box>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                      {planOfCareLoading ? 'Loading conditions...' : 'No chronic conditions recorded'}
                    </Typography>
                  );
                })()}

                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                  Allergies:
                </Typography>
                {prescription.patientInfo?.allergies && prescription.patientInfo.allergies.length > 0 ? (
                  <Box component="ul" sx={{ mt: 0, mb: 0, pl: 2 }}>
                    {prescription.patientInfo.allergies.map((allergy, index) => (
                      <Typography component="li" variant="body2" key={index} color="error">
                        {allergy}
                      </Typography>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">No allergies recorded</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                avatar={<CalendarToday color="primary" />}
                title="Prescription Details"
                  titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
                sx={{ pb: 1 }}
              />
              <CardContent sx={{ pt: 0 }}>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AccessTimeIcon fontSize="small" sx={{ mr: 1, color: theme.palette.info.main, fontSize: '0.9rem' }} />
                  Requested: {formatDate(prescription.requestDate)}
                </Typography>
                
                {prescription.claimedDate && (
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <AssignmentIcon fontSize="small" sx={{ mr: 1, color: theme.palette.info.main, fontSize: '0.9rem' }} />
                    Claimed: {formatDate(prescription.claimedDate)}
                  </Typography>
                )}
                
                {prescription.claimedBy && (
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Person fontSize="small" sx={{ mr: 1, color: theme.palette.info.main, fontSize: '0.9rem' }} />
                    Claimed by: {prescription.claimedBy}
                  </Typography>
                )}
                
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                  Request Notes:
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>
                  {prescription.notes || 'No notes provided'}
                </Typography>
              </CardContent>
            </Card>

            {/* Plan of Care Section - Summary (only for claimed prescriptions) */}
            {planOfCare && prescription.status === 'CLAIMED' && (
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardHeader
                  avatar={<MedicalServicesOutlined color="primary" />}
                  title="Plan of Care Summary"
                  titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
                  sx={{ pb: 1 }}
                />
                <CardContent sx={{ pt: 0 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Plan Status:
        </Typography>
                  <Chip 
                    label="Active Plan Available" 
                    color="success" 
                    size="small"
                    sx={{ mb: 2 }}
                  />
                  
                  {planOfCare.LastUpdated && (
                    <>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                        Last Updated:
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {formatDate(planOfCare.LastUpdated)}
                      </Typography>
                    </>
                  )}

                  {planOfCare.Pathways && (() => {
                    try {
                      const pathways = JSON.parse(planOfCare.Pathways);
                      return (
                        <>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                            Active Pathways:
                </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {pathways.length} clinical pathway{pathways.length !== 1 ? 's' : ''} identified
                          </Typography>
                    </>
                      );
                    } catch {
                      return null;
                    }
                  })()}

                  {/* Health Documents Button */}
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      startIcon={healthDocumentsLoading ? <CircularProgress size={16} /> : <FolderIcon />}
                      onClick={handleCheckHealthDocuments}
                      disabled={healthDocumentsLoading || !planOfCare.profileID}
                      size="small"
                      fullWidth
                      sx={{ 
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 500
                      }}
                    >
                      {healthDocumentsLoading ? 'Loading...' : 'Check Health Documents'}
                    </Button>
                  </Box>

                  {planOfCare.MedicalTerms && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                        Clinical Summary:
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        fontStyle: 'italic',
                        maxHeight: '80px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical'
                      }}>
                        {planOfCare.MedicalTerms.substring(0, 150)}...
                      </Typography>
                    </>
                  )}
                </CardContent>
              </Card>
                  )}

            {/* Health Documents Section - positioned under Plan of Care */}
            {(showHealthDocuments || healthDocuments) && (
              <Card sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
                <CardHeader
                  avatar={
                    <Avatar sx={{ bgcolor: 'info.main' }}>
                      <FolderIcon />
                    </Avatar>
                  }
                  title="Health Documents"
                  titleTypographyProps={{ variant: 'h6', fontWeight: 'bold' }}
                  subheader={`Documents for ${prescription?.userDetail?.FirstName || 'Patient'} ${prescription?.userDetail?.LastName || ''}`}
                  action={
                    <IconButton onClick={() => setShowHealthDocuments(!showHealthDocuments)}>
                      {showHealthDocuments ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  }
                />
                <Collapse in={showHealthDocuments}>
                  <CardContent>
                  {healthDocumentsError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {healthDocumentsError}
                    </Alert>
                  )}

                  {healthDocuments && healthDocuments.files ? (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Found {healthDocuments.files.length} document{healthDocuments.files.length !== 1 ? 's' : ''} 
                        {healthDocuments.files.length > 5 && ' (scroll to see more)'}
                      </Typography>
                      
                      {/* Scrollable container for documents */}
                      <Box
                        sx={{
                          maxHeight: '400px', // Height for approximately 5 documents
                          overflowY: 'auto',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          '&::-webkit-scrollbar': {
                            width: '8px',
                          },
                          '&::-webkit-scrollbar-track': {
                            backgroundColor: 'grey.100',
                            borderRadius: '4px',
                          },
                          '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'grey.400',
                            borderRadius: '4px',
                            '&:hover': {
                              backgroundColor: 'grey.600',
                            },
                          },
                        }}
                      >
                        <List sx={{ py: 0 }}>
                          {healthDocuments.files.map((file, index) => {
                            const getFileIcon = (fileType) => {
                              const type = fileType?.toLowerCase();
                              if (type === 'pdf') return <PdfIcon color="error" />;
                              if (['jpg', 'jpeg', 'png', 'gif'].includes(type)) return <ImageIcon color="primary" />;
                              if (['doc', 'docx', 'txt'].includes(type)) return <DescriptionIcon color="info" />;
                              return <FileIcon />;
                            };

                            return (
                              <ListItem 
                                key={index} 
                                divider={index < healthDocuments.files.length - 1}
                                sx={{ 
                                  py: 1.5,
                                  '&:hover': {
                                    backgroundColor: 'action.hover',
                                  }
                                }}
                              >
                                <ListItemIcon sx={{ minWidth: '40px' }}>
                                  {getFileIcon(file.FileType)}
                                </ListItemIcon>
                                <ListItemText
                                  primary={
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {file.FileName}
                                    </Typography>
                                  }
                                  secondary={
                                    <Box>
                                      <Typography variant="caption" display="block">
                                        Size: {file.SizeKB} KB • Type: {file.FileType?.toUpperCase() || 'Unknown'}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        Last Modified: {file.LastModified}
                                      </Typography>
                                    </Box>
                                  }
                                />
                                <Button
                                  component="a"
                                  href={file.PreviewURL}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  variant="outlined"
                                  size="small"
                                  startIcon={<VisibilityIcon />}
                                  sx={{ 
                                    minWidth: 'auto',
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    ml: 1,
                                    textTransform: 'none',
                                    fontSize: '0.75rem',
                                    borderColor: 'primary.main',
                                    color: 'primary.main',
                                    '&:hover': {
                                      backgroundColor: 'primary.main',
                                      color: 'white',
                                      borderColor: 'primary.main',
                                    }
                                  }}
                                >
                                  View
                                </Button>
                              </ListItem>
                            );
                          })}
                        </List>
                      </Box>
                      
                      {/* Document count summary at bottom */}
                      {healthDocuments.files.length > 5 && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                          Showing all {healthDocuments.files.length} documents • Scroll up to see more
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No documents available
                    </Typography>
                  )}
                  </CardContent>
                </Collapse>
              </Card>
            )}
            </Box>
          </Grid>
        </Grid>

        

        <Divider sx={{ my: 3 }} />

        <Box>
          <Typography variant="h6" gutterBottom>
            Medications ({prescription.medications?.length || 0})
        </Typography>
          <Divider sx={{ mb: 2 }} />
          
          {prescription.medications && prescription.medications.length > 0 ? (
            <Grid container spacing={2}>
              {prescription.medications.map((medication, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  {renderMedicationItem(medication, index)}
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography variant="body1" color="text.secondary" sx={{ my: 2, textAlign: 'center' }}>
              No medications found for this prescription.
            </Typography>
                  )}
                </Box>

        {prescription.status === 'CLAIMED' && (
          <>
            <Divider sx={{ my: 3 }} />
            
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
                Issue Prescription
              </Typography>
              <TextField
                fullWidth
                label="Doctor's Notes"
                multiline
                rows={4}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                variant="outlined"
                placeholder="Add any notes or instructions for the patient..."
                sx={{ mb: 2 }}
              />
              
              {/* Embedded Prescription Software */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" component="h3" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MedicalServicesOutlined color="primary" />
                  Certified Prescription Software
                </Typography>
                <Alert 
                  severity="info" 
                  sx={{ 
                    mb: 2, 
                    borderRadius: 1,
                    '& .MuiAlert-message': {
                      fontSize: '0.875rem'
                    }
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                    Patient: {prescription.patientName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Use the certified prescription software below to create the official prescription.
                  </Typography>
                </Alert>
                <Box sx={{ 
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  overflow: 'hidden',
                  height: '600px',
                  bgcolor: 'background.paper'
                }}>
                  <iframe
                    src="https://www.ccare.eu/icare/log_ent.home"
                    width="100%"
                    height="100%"
                    style={{ 
                      border: 'none',
                      display: 'block'
                    }}
                    sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
                    title="Certified Prescription Software"
                    loading="lazy"
                  />
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button variant="outlined" onClick={onClose}>
                  Cancel
                </Button>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={handleOpenConfirmDialog}
                  startIcon={<CheckCircleIcon />}
                >
                  Issue Prescription
                </Button>
              </Box>
            </Box>
          </>
        )}

        {prescription.status !== 'CLAIMED' && prescription.status !== 'ISSUED' && (
          <Button
            variant="contained"
            color="secondary"
            onClick={handleClaimPrescription}
            startIcon={<AssignmentOutlined />}
            disabled={isSubmitting}
            sx={{ mt: 3 }}
          >
            Claim Prescription
            {isSubmitting && <CircularProgress size={24} sx={{ ml: 1 }} />}
          </Button>
        )}
      </Paper>

      <Dialog
        open={confirmDialogOpen}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Confirm Prescription Issuance
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to issue this prescription for {prescription.patientName || 'the patient'}? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={handleIssuePrescription} 
            color="primary" 
            variant="contained"
            disabled={issuingPrescription}
            autoFocus
          >
            {issuingPrescription ? 'Processing...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

PrescriptionDetail.propTypes = {
  prescription: PropTypes.shape({
    requestId: PropTypes.string.isRequired,
    patientName: PropTypes.string.isRequired,
    patientId: PropTypes.string.isRequired,
    patientInfo: PropTypes.shape({
      age: PropTypes.number.isRequired,
      gender: PropTypes.string.isRequired,
      bloodType: PropTypes.string.isRequired,
      allergies: PropTypes.arrayOf(PropTypes.string).isRequired,
      chronicConditions: PropTypes.arrayOf(PropTypes.string).isRequired
    }).isRequired,
    medications: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        dosage: PropTypes.string.isRequired,
        frequency: PropTypes.string.isRequired,
        condition: PropTypes.string.isRequired
      })
    ).isRequired,
    requestDate: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    notes: PropTypes.string,
    doctorId: PropTypes.string
  }).isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onIssuePrescription: PropTypes.func.isRequired,
  onIssued: PropTypes.func.isRequired,
  onClaimed: PropTypes.func.isRequired
};

export default PrescriptionDetail; 